import { DateTime } from 'luxon';

import { accountsActions, accountsTypes } from 'app-components/flux/accounts';
import store from 'app-components/utils/redux-store';

import deterministicGuid from './deterministicGuid';
import { mergeMintImportSharedState } from './mintImportSharedState';

const typesMap = {
  CashAccount: {
    type: 'BANK',
    default: 'OTHER_BANK',
    CashAccount: 'OTHER_BANK',
  },
  BankAccount: {
    type: 'BANK',
    default: 'OTHER_BANK',
    CD: 'CD',
    CHECKING: 'CHECKING',
    MONEY_MARKET: 'MONEY_MARKET',
    OTHER: 'OTHER_BANK',
    SAVINGS: 'SAVINGS',
    OVERDRAFT: 'OTHER_BANK',
    CASH_MANAGEMENT: 'CASH_MANAGEMENT',
  },
  CreditAccount: {
    type: 'CREDIT',
    default: 'OTHER_CREDIT',
    UNKNOWN: 'OTHER_CREDIT',
    CREDIT_CARD: 'CREDIT_CARD',
    LINE_OF_CREDIT: 'LINE_OF_CREDIT',
    HOME_EQUITY_LINE_OF_CREDIT: 'LINE_OF_CREDIT',
  },
  LoanAccount: {
    type: 'LOAN',
    default: 'LOAN',
    AUTO: 'AUTO_LOAN',
    HOME_EQUITY_LINE_OF_CREDIT: 'HOME_EQUITY',
    INSTALLMENT: 'LOAN',
    LIFE_INSURANCE: 'LOAN',
    LINE_OF_CREDIT: 'LOAN',
    LOAN: 'LOAN',
    MORTGAGE: 'MORTGAGE',
    OTHER: 'LOAN',
    PERSONAL: 'STUDENT_LOAN',
    STUDENT: 'STUDENT_LOAN',
    UNKNOWN: 'LOAN',
    CONSUMER: 'CONSUMER_LOAN',
    COMMERCIAL: 'LOAN',
    MILITARY: 'MILITARY_LOAN',
    SMB: 'SMB',
    CONSTRUCTION: 'CONSTRUCTION_LOAN',
  },
  InvestmentAccount: {
    type: 'INVESTMENT',
    default: 'OTHER_INVESTMENTS',
    _401K: '401K',
    _403B: '403B',
    _529: '529',
    TAXABLE_BROKERAGE: 'BROKERAGE',
    TRADITIONAL_IRA: 'IRA',
    OTHER: 'OTHER_INVESTMENTS',
    ROLLOVER_IRA: 'IRA',
    ROTH_IRA: 'ROTH_IRA',
    SEP: 'SEP_IRA',
    TAXABLE: 'BROKERAGE',
    NON_TAXABLE: 'OTHER_INVESTMENTS',
    EMPLOYER: 'OTHER_INVESTMENTS',
  },
  RealEstateAccount: {
    type: 'REAL_ESTATE',
    default: 'UNKNOWN',
    ASSET: 'UNKNOWN',
  },
  VehicleAccount: {
    type: 'VEHICLE',
    default: 'UNKNOWN',
    ASSET: 'UNKNOWN',
  },
  OtherPropertyAccount: {
    type: 'OTHER_ASSET',
    default: 'UNKNOWN',
    ASSET: 'UNKNOWN',
    LIABILITY: 'UNKNOWN',
  },
  InsuranceAccount: {
    type: 'OTHER_ASSET',
    default: 'UNKNOWN',
    InsuranceAccount: 'UNKNOWN',
  },
  default: {
    type: 'BANK', // QCS rejects 'UNKNOWN', so fallback to BANK
    default: 'UNKNOWN',
  },
};

const importAccounts = async (fileJS, simplifiLoginsByMintId) => {
  const key = 'ACCOUNTS';
  let simplifiAccountsByMintId;
  try {
    const mintAccountsById = fileJS.accounts.reduce((result, mintAccount) => {
      const account = {
        ...mintAccount,
        // clientId: uuidv4().toUpperCase(),
        clientId: deterministicGuid(mintAccount.id),
        id: mintAccount.id,
        name:
          `${mintAccount.name}` +
          `${
            mintAccount.cpAccountName && mintAccount.name !== mintAccount.cpAccountName
              ? ` (${mintAccount.cpAccountName})`
              : ''
          }` +
          `${mintAccount.cpAccountNumberLast4 ? ` ${mintAccount.cpAccountNumberLast4}` : ''}`,
      };
      return result.set(account.id, account);
    }, new Map());
    const mintAccounts = [...mintAccountsById.values()];
    mintAccountsById.forEach((mintAccount) => {
      let uniqueName = mintAccount.name;
      for (
        let index = 2;
        mintAccounts.find((login) => login.id !== mintAccount.id && login.name === uniqueName);
        index += 1
      ) {
        // eslint-disable-line @typescript-eslint/no-loop-func
        uniqueName = `${mintAccount.name}${index}`;
      }
      mintAccount.name = uniqueName; // eslint-disable-line no-param-reassign
    });

    mergeMintImportSharedState({ key, progress: 20 });

    const accountsResponse = await new Promise((resolve, reject) => {
      store.dispatch(
        accountsActions.batchAccounts(
          mintAccounts.map((mintAccount) => {
            const institutionLoginId = simplifiLoginsByMintId.get(mintAccount.fiLoginId)?.id;
            assert(institutionLoginId, 'institutionLoginId not found for fiLoginId', { mintAccount });
            const subTypes =
              assert(typesMap[mintAccount.type], 'unexpected mint account type', { mintAccount }) || typesMap.default;
            const mintSubType =
              mintAccount.bankAccountType ||
              mintAccount.creditAccountType ||
              mintAccount.loanType ||
              mintAccount.investmentType ||
              mintAccount.propertyType ||
              mintAccount.type;
            const subType =
              assert(subTypes[mintSubType], 'unexpected mint account subType', {
                type: subTypes.type,
                mintSubType,
              }) || subTypes.default;
            return accountsTypes.mkAccount(subTypes.type, {
              clientId: mintAccount.clientId,
              name: mintAccount.name,
              institutionLoginId,
              type: subTypes.type,
              subType,
              isExcludedFromReports: !mintAccount.planningTrendsVisible, // or isHiddenFromPlanningTrends
              isExcludedFromAccountBar: !mintAccount.isVisible || mintAccount.isClosed,
              currency: mintAccount.currency,
              isClosed: mintAccount.isClosed,
              accountNumberMasked: mintAccount.cpAccountNumberLast4,
              onlineBalance: mintAccount.value,
              onlineBalanceAt: DateTime.now().toISO(),
              ...(['REAL_ESTATE', 'VEHICLE', 'OTHER_ASSET'].includes(subTypes.type) && {
                balanceAsOf: mintAccount.value,
                balanceAsOfOn: DateTime.now().toISODate(),
              }),
              currentBalanceAsOf: 0, // QCS hack to trigger calc
              currentBalanceAsOfOn: '1970-01-01',
              // aggregators: List([{
              //   id: mintAccount.cpId,
              //   channel: 'DEFAULT',
              //   accountName: mintAccount.cpAccountName,
              //   accountNumberMasked: mintAccount.cpAccountNumberLast4,
              // }]),
            });
          }),
          { resolve, reject },
        ),
      );
    });

    mergeMintImportSharedState({ key, progress: 80 });

    simplifiAccountsByMintId = accountsResponse.data.items.reduce((simplifiAccounts, simplifiAccount) => {
      const mintAccountFound = mintAccounts.find(
        (mintAccount) => mintAccount.clientId === simplifiAccount.resource.clientId,
      );
      assert(mintAccountFound, 'mint account not found', { simplifiAccount });
      return mintAccountFound
        ? simplifiAccounts.set(
            mintAccountFound.id,
            accountsTypes.mkAccount(
              assert(simplifiAccount.resource.resource?.type, 'account type missing', { simplifiAccount }),
              simplifiAccount.resource.resource,
            ),
          )
        : simplifiAccounts;
    }, new Map());

    // special handling for closed accounts (qcs doesn't directly accept isClosed: true)
    // eslint-disable-next-line no-restricted-syntax
    for (const mintAccount of mintAccounts) {
      if (mintAccount.isClosed) {
        const id = simplifiAccountsByMintId.get(mintAccount.id)?.id;
        if (assert(id, 'account not found', { mintAccount })) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve, reject) => {
            store.dispatch(accountsActions.performAccountAction({ id, qcsAction: 'close' }, { resolve, reject }));
          });
        }
      }
    }

    mergeMintImportSharedState({ key, progress: 100, [key]: { importedCount: simplifiAccountsByMintId.size } });
  } catch (error) {
    assert(false, error);
    mergeMintImportSharedState({ error: key, [key]: { error } });
  }

  return simplifiAccountsByMintId;
};

export default importAccounts;
